// PaymentsListPage — раздел «Платежи» (V12 U-35, волна R66; V14 W7 §3.7/§3.8 — расширение).
// Таблица (№/Дата/Сумма/Источник/Привязка/Счёт/Фискальный №/Статус/Действия) + фильтр статуса
// (чипы) + фильтр счёта (?invoiceId= → чип «Счёт: INV-…») + живой поиск q+fields (NUMBER,
// канон V13 U-38 SearchFieldBar) + сортировка (whitelist paymentNumber|paymentDate|amount|
// createdAt, '-' = desc — §3.4; default created_at DESC) + пагинация, кнопки «Создать платёж»
// (manual, source=MANUAL) и «Загрузить чек». Фильтр привязки — через URL ?entityType=&entityId=.
// U-15: привязка — названия сущностей (companyName/contactName/dealTitle/лид из leads-map), не id.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { paymentsApi, type Payment, type PaymentEntityType, type PaymentStatus } from '@/api/payments';
import { invoicesApi } from '@/api/invoices';
import { useLeadMap, resolveLeadTitle } from '@/api/leads';
import { AmountChip } from '@/pages/crm/components/AmountChip';
import { PaymentNewDialog } from './components/PaymentNewDialog';
import { ReceiptUploadDialog } from './components/ReceiptUploadDialog';
import { PaymentStatusChip, paymentStatusColor } from './components/PaymentStatusChip';
import type { PaymentBinding } from './components/EntityBindingSelect';
import { formatPaymentEntityType } from './components/EntityBindingSelect';
import { SearchFieldBar, type SearchFieldValue } from '@/components/SearchFieldBar';
import { formatDate, formatInvoiceNumber, formatPaymentSource, formatPaymentStatus } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Сколько миллисекунд показывать success-бейдж «Платёж зафиксирован». */
const SUCCESS_ALERT_MS = 4000;

/** Порядок статусов в чипах фильтра + цвета чипов (канон списка лидов). */
const PAYMENT_STATUS_ORDER: PaymentStatus[] = ['PENDING', 'CONFIRMED', 'FAILED'];

/** Для сущности-фильтра: только whitelist entityType (защита от мусора в URL). */
const VALID_ENTITY_TYPES: PaymentEntityType[] = ['COMPANY', 'CONTACT', 'DEAL', 'LEAD'];

/** Поля сортировки (whitelist §3.4: paymentNumber|paymentDate|amount|createdAt; default created_at DESC). */
const SORT_FIELDS = ['paymentNumber', 'paymentDate', 'amount', 'createdAt'] as const;
type PaymentSortField = (typeof SORT_FIELDS)[number];

function isEntityType(value: string | null): value is PaymentEntityType {
  return VALID_ENTITY_TYPES.includes(value as PaymentEntityType);
}

/** Название привязки платежа (U-15: компания/контакт/сделка — из DTO; лид — из leads-map). */
function paymentBindingLabel(payment: Payment, leadMap: Map<string, string> | undefined): string {
  if (payment.companyId) return payment.companyName ?? t('common.unknown');
  if (payment.contactId) return payment.contactName ?? t('common.unknown');
  if (payment.dealId) return payment.dealTitle ?? t('common.unknown');
  if (payment.leadId) return resolveLeadTitle(leadMap, payment.leadId);
  return '—';
}

/** Страница раздела «Платежи». */
export function PaymentsListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const entityType = isEntityType(searchParams.get('entityType')) ? (searchParams.get('entityType') as PaymentEntityType) : null;
  const entityId = searchParams.get('entityId');
  const invoiceId = searchParams.get('invoiceId');
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [search, setSearch] = useState<SearchFieldValue>({ q: '', fields: [] });
  const [sortField, setSortField] = useState<PaymentSortField | ''>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualBinding, setManualBinding] = useState<PaymentBinding | null>(null);
  const [success, setSuccess] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const leadMap = useLeadMap();

  /** Счёт по invoiceId — для чипа «Счёт: INV-…» (№ счёта в DTO платежа нет — LEFT JOIN только номер). */
  const invoiceQuery = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoicesApi.get(invoiceId as string),
    enabled: Boolean(invoiceId),
  });
  const invoiceFilterChipLabel = invoiceQuery.data
    ? `${t('payments.invoice')}: ${formatInvoiceNumber(invoiceQuery.data.invoiceNumber, invoiceQuery.data.invoiceDate)}`
    : `${t('payments.invoice')}: ${invoiceId ?? ''}`;

  /** Проводной sort-параметр: '-' = desc (SSOT §3.4; канон deal-list field,direction). */
  const sortParam = sortField ? `-${sortField}` : undefined;

  const query = useQuery({
    queryKey: [
      'payments',
      page,
      status ?? null,
      entityType ?? null,
      entityId ?? null,
      invoiceId ?? null,
      search.q,
      search.fields.join(','),
      sortParam ?? null,
    ],
    queryFn: () =>
      paymentsApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(entityType && entityId ? { entityType, entityId } : {}),
        ...(invoiceId ? { invoiceId } : {}),
        ...(search.q ? { q: search.q } : {}),
        ...(search.fields.length > 0 ? { fields: search.fields.join(',') } : {}),
        ...(sortParam ? { sort: sortParam } : {}),
      }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ payment, next }: { payment: Payment; next: PaymentStatus }) =>
      paymentsApi.update(payment.id, { status: next, expectedUpdatedAt: payment.updatedAt }),
    onSuccess: () => {
      setStatusError(null);
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (error) => {
      // B2: 409 (stale version) → refetch, пользователь не в тупике повторных 409.
      setStatusError(extractApiErrorMessage(error, t('payments.update.error')));
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });

  /** Клик по чипу статуса: выбрать/снять (одиночный фильтр — контракт status=). */
  const handleStatusToggle = (next: PaymentStatus): void => {
    setPage(0);
    setStatus((prev) => (prev === next ? null : next));
  };

  /** Сброс фильтра привязки (chip «Фильтр: …» → ?entityType/entityId очищаются). */
  const clearEntityFilter = (): void => {
    setPage(0);
    setSearchParams({});
  };

  /** Сброс фильтра счёта (chip «Счёт: …» → ?invoiceId очищается; остальные параметры сохраняются). */
  const clearInvoiceFilter = (): void => {
    setPage(0);
    const next = new URLSearchParams(searchParams);
    next.delete('invoiceId');
    setSearchParams(next);
  };

  /** Смена поиска/полей: сброс пагинации (q — после debounce внутри SearchFieldBar). */
  const handleSearchChange = (value: SearchFieldValue): void => {
    setPage(0);
    setSearch(value);
  };

  /** Смена сортировки: сброс пагинации. */
  const handleSortChange = (value: PaymentSortField | ''): void => {
    setPage(0);
    setSortField(value);
  };

  /** Успех загрузки чека со списка: бейдж (invalidate — в ReceiptUploadDialog). */
  const handleUploaded = (): void => {
    setSuccess(true);
  };

  // Авто-скрытие success-бейджа (единый контракт с PaymentBlock).
  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(false), SUCCESS_ALERT_MS);
    return () => clearTimeout(timer);
  }, [success]);

  /** Опции сортировки (whitelist §3.4; default — created_at DESC на backend). */
  const sortOptions: Array<{ value: PaymentSortField; label: string }> = [
    { value: 'paymentNumber', label: t('payments.paymentNumber') },
    { value: 'paymentDate', label: t('payments.paymentDate') },
    { value: 'amount', label: t('payments.amount') },
    { value: 'createdAt', label: t('payments.sort.createdAt') },
  ];

  return (
    <Box>
      <PageHeader
        title={t('payments.title')}
        subtitle={t('payments.subtitle')}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              aria-label={t('payments.upload')}
              onClick={() => setUploadOpen(true)}
            >
              {t('payments.upload')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              aria-label={t('payments.create')}
              onClick={() => setCreateOpen(true)}
            >
              {t('payments.create')}
            </Button>
          </Stack>
        }
      />
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{t('payments.upload.success')}</Alert> : null}
      {statusError ? <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert> : null}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('payments.status')}:
        </Typography>
        {PAYMENT_STATUS_ORDER.map((s) => {
          const selected = status === s;
          return (
            <Chip
              key={s}
              data-testid={`payment-status-${s}`}
              label={formatPaymentStatus(s, t)}
              clickable
              onClick={() => handleStatusToggle(s)}
              color={selected ? paymentStatusColor(s) : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              size="small"
            />
          );
        })}
        {entityType && entityId ? (
          <Chip
            label={`${t('payments.entity')}: ${formatPaymentEntityType(entityType)}`}
            color="primary"
            onDelete={clearEntityFilter}
            size="small"
          />
        ) : null}
        {invoiceId ? (
          <Chip label={invoiceFilterChipLabel} color="primary" onDelete={clearInvoiceFilter} size="small" />
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <SearchFieldBar
          fieldsOptions={[{ key: 'number', label: t('payments.paymentNumber') }]}
          value={search}
          onChange={handleSearchChange}
          placeholder={t('payments.search')}
          searchAriaLabel={t('payments.search')}
        />
        <TextField
          select
          size="small"
          label={t('payments.sort')}
          value={sortField}
          onChange={(e) => handleSortChange(e.target.value as PaymentSortField | '')}
          inputProps={{ 'aria-label': t('payments.sort') }}
          sx={{ minWidth: 180 }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {query.isLoading ? (
        <LoadingSpinner label={t('payments.loading')} />
      ) : query.isError ? (
        <EmptyState
          title={t('payments.loadError')}
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState title={t('payments.empty')} description={t('payments.emptyDesc')} />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>{t('payments.paymentDate')}</TableCell>
                  <TableCell>{t('payments.amount')}</TableCell>
                  <TableCell>{t('payments.source')}</TableCell>
                  <TableCell>{t('payments.entity')}</TableCell>
                  <TableCell>{t('payments.invoice')}</TableCell>
                  <TableCell>{t('payments.fiscalDocNumber')}</TableCell>
                  <TableCell>{t('payments.status')}</TableCell>
                  <TableCell align="right">{t('payments.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(query.data.content as Payment[]).map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>#{payment.paymentNumber ?? '—'}</TableCell>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>
                      <AmountChip amount={payment.amount} currency={payment.currency} />
                    </TableCell>
                    <TableCell>{formatPaymentSource(payment.source, t)}</TableCell>
                    <TableCell>{paymentBindingLabel(payment, leadMap)}</TableCell>
                    <TableCell>
                      {payment.invoiceId
                        ? formatInvoiceNumber(payment.invoiceNumber, payment.invoiceDate)
                        : '—'}
                    </TableCell>
                    <TableCell>{payment.fiscalDocNumber ?? '—'}</TableCell>
                    <TableCell>
                      <PaymentStatusChip status={payment.status} />
                    </TableCell>
                    <TableCell align="right">
                      {payment.status === 'PENDING' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t('payments.confirm')}>
                            <IconButton
                              size="small"
                              aria-label={t('payments.confirm')}
                              disabled={statusMutation.isPending}
                              onClick={() => statusMutation.mutate({ payment, next: 'CONFIRMED' })}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('payments.reject')}>
                            <IconButton
                              size="small"
                              aria-label={t('payments.reject')}
                              disabled={statusMutation.isPending}
                              onClick={() => statusMutation.mutate({ payment, next: 'FAILED' })}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('payments.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('payments.of')} ${count}`}
          />
        </Paper>
      )}

      <PaymentNewDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setManualBinding(null);
        }}
        preset={manualBinding}
      />
      <ReceiptUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        preset={null}
        onUploaded={handleUploaded}
        onCreateManual={(binding) => {
          setUploadOpen(false);
          setManualBinding(binding);
          setCreateOpen(true);
        }}
      />
    </Box>
  );
}
